<template>
    <md-card>
        <md-card-header class="md-card-header-icon md-card-header-blue">
            <div class="md-layout md-gutter">
                <div class="md-layout-item">
                    <div class="card-icon">
                        <font-awesome-icon class="md-icon" :icon="['fa', 'map-marker-alt']" />
                    </div>
                    <h4 class="title">Carte</h4>
                </div>
            </div>
        </md-card-header>
        <md-card-content>
            <div class="mb-1">Pour vous déplacer sur la carte, maintenez le clic gauche de la souris enfoncé et déplacez le curseur.</div>
            <div class="mb-2">Pour zoomer sur la carte, maintenez la touche <kbd>Ctrl</kbd> et utilisez la molette de la souris.</div>
            <div class="map-container">
                <div id="map"></div>
            </div>
            <div class="hidden">
                <div id="popup" ref="popup">
                    <div v-if="popupData !== null">
                        <p class="font-medium text-sm">{{ popupData.city }}</p>
                        <template v-if="popupInterventions(true).length > 0">
                            <md-divider class="my-1.5 -mx-3"></md-divider>
                            <p class="font-medium text-success">Interventions dans le panier / prévues</p>
                            <div v-for="(intervention) in popupInterventions(true)" :key="intervention.id">
                                <span class="font-medium">{{ intervention.startTime }} / {{ getCartInterventionEndTime(intervention) }}</span>
                                <span class="ml-1">- {{ intervention.intervention.site_name}}</span>
                            </div>
                        </template>
                        <template v-if="popupInterventions(false).length > 0">
                            <md-divider class="mt-2 mb-1.5 -mx-3"></md-divider>
                            <p class="font-medium text-primary">Interventions à planifier</p>
                            <div v-for="(intervention) in popupInterventions(false)" :key="intervention.id" class="mt-1">
                                <div class="flex items-center justify-between mb-2">
                                    <div class="leading-4">
                                        <div>
                                            <span class="font-medium">Site</span> : {{ intervention.site_name }}
                                        </div>
                                        <div>
                                            <span class="font-medium">Durée</span> : {{ hoursAndMinutes(intervention.duration) }}
                                            <span class="ml-1 mr-1.5">|</span>
                                            <span class="font-medium">Mois</span> : {{ formatMonthName(intervention.month) }}
                                        </div>
                                    </div>
                                    <md-button class="md-icon-button md-small-icon md-rounded-icon my-0 md-success"
                                               :disabled="!cartIsReady"
                                               @click="addToCart(intervention)">
                                        <font-awesome-icon class="md-icon" :icon="['fa', 'plus']" />
                                    </md-button>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </md-card-content>
    </md-card>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import dateTimeFormattingMixin from '@/mixins/date-time-formatting-mixin'

export default {
    name: "ManagementMap",
    components: {
    },
    mixins: [ dateTimeFormattingMixin ],
    props: {
        cart: Object,
        cartIsReady : Boolean,
        displayedTableRows: Array
    },
    data() {
        return {
            loading: false,
            location: "",
            accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
            center: [4.860903746023085, 44.920630581624884],
            map: {},
            markers: [],
            popup: null,
            popupData: null
        }
    },
    mounted() {
        this.createMap()
    },
    methods: {
        popupInterventions(inCart) {
            if(inCart) {
                return this.popupData.interventions.filter(intervention => intervention.intervention)
            } else {
                return this.popupData.interventions.filter(intervention => !intervention.intervention)
            }

        },
        async createMap() {
            mapboxgl.accessToken = this.accessToken;
            this.map = new mapboxgl.Map({
                container: "map",
                style: "mapbox://styles/mapbox/streets-v11",
                center: this.center,
                zoom: 8,
            });
            this.map.on('load', () => {
                this.map.setLayoutProperty('country-label', 'text-field', [
                    'get',
                    `name_fr`
                ]);
                this.addMarkers()
            })
            this.map.on('wheel', (event) => {
                if (this.map) {
                    if (event.originalEvent.ctrlKey === true) {
                        event.originalEvent.preventDefault()
                        this.map['scrollZoom'].enable();
                    } else {
                        this.map['scrollZoom'].disable();
                    }
                }
            });
            // disable map rotation using right click + drag
            this.map.dragRotate.disable();
        },
        addMarkers () {
            this.markers.forEach(marker => marker.remove())
            const sites = []
            if (this.cart.interventions && this.cart.interventions.length > 0) {
                this.cart.interventions.forEach ((intervention) => {
                    const site = sites.find(o => (o.latitude === intervention.intervention.latitude && o.longitude === intervention.intervention.longitude))
                    if (!site) {
                        const newSite = {
                            site_id: intervention.intervention.site_id,
                            latitude: intervention.intervention.latitude,
                            longitude: intervention.intervention.longitude,
                            city: intervention.intervention.city,
                            type: 'cart',
                            interventions: [intervention],
                            order: sites.length + 1
                        }
                        sites.push(newSite)
                        this.addMarker(newSite)
                    } else {
                        site.interventions.push(intervention)
                    }
                })
            }

            if(this.displayedTableRows && this.displayedTableRows.length > 0) {
                this.displayedTableRows.forEach ((intervention) => {
                    const site = sites.find(o => (o.latitude === intervention.latitude && o.longitude === intervention.longitude))
                    if (!site) {
                        const newSite = {
                            site_id: intervention.site_id,
                            latitude: intervention.latitude,
                            longitude: intervention.longitude,
                            city: intervention.city,
                            type: 'tableRow',
                            interventions: [intervention]
                        }
                        sites.push(newSite)
                        this.addMarker(newSite)
                    } else {
                        //Push the intervention in the array if not already present (in the case of an intervention present in the previsions index AND in the cart)
                        const interventionIsStored = site.interventions.find(o => {
                            if (o.intervention) {
                                if (o.intervention.prevision_id) {
                                    return o.intervention.prevision_id === intervention.prevision_id
                                }

                                return o.intervention.intervention_id === intervention.intervention_id
                            } else {
                                if (o.prevision_id) {
                                    return o.prevision_id === intervention.prevision_id
                                }
                                return o.intervention_id === intervention.intervention_id
                            }
                        })
                        if (!interventionIsStored) {
                            site.interventions.push(intervention)
                        }
                    }
                })
            }
        },
        addMarker (site) {
            // create a HTML element for each feature
            const markerNode = document.createElement('div');
            markerNode.className = 'marker';
            markerNode.innerHTML = '<span><b></b></span>';

            if (site.type === 'cart') {
                markerNode.innerHTML = '<span><b>' + site.order + '</b></span>'
                markerNode.classList.add('cartMarker')
                markerNode.style.zIndex = 50 - site.order
            } else {
                markerNode.innerHTML = '<span><b></b></span>';
                markerNode.classList.add('tableRowMarker')
            }

            // Create a marker and add it to the map.
            const marker = new mapboxgl.Marker(markerNode)
                .setLngLat([site.longitude, site.latitude])
                .addTo(this.map);

            markerNode.addEventListener('click', () => {
                this.createPopup(marker, site)
            },)
            this.markers.push(marker)
        },

        createPopup (marker, site) {
            if (this.popup) {
                this.popup.remove()
            }
            const popupContainer = this.$refs['popup']
            const popup = new mapboxgl.Popup({ offset: 25 })
                .setDOMContent(popupContainer)
                .setMaxWidth('250')
            this.popup = popup
            this.popupData = site
            marker.setPopup(popup)
        },
        getCartInterventionEndTime (intervention) {
            return this.$moment(intervention.startTime, 'HH:mm').add(intervention.intervention.duration, 'minutes').format('HH:mm')
        },
        addToCart(intervention) {
            this.$emit('updating')
            let data = {
                checked: true,
                prevision_id: intervention.prevision_id,
                client_num: intervention.client_num,
                client_name: intervention.client_name,
                site_id: intervention.site_id,
                site_name: intervention.site_name,
                latitude: intervention.latitude,
                longitude: intervention.longitude,
                sector_name: intervention.sector_name,
                city: intervention.city,
                duration: intervention.duration,
                intervention_type: intervention.intervention_type,
                intervention_type_id: intervention.intervention_type_id,
                intervention_id: intervention.intervention_id,
            }
            this.$emit('update-cart-interventions', data)
        },
    },
    watch: {
        displayedTableRows: {
            handler () {
                this.addMarkers()
            },
            deep: true
        },
        cart: {
            handler () {
                this.addMarkers()
            },
            deep: true
        }
    },
}
</script>

<style scoped lang="scss">

@import '~mapbox-gl/dist/mapbox-gl.css';

::v-deep {
    .marker {
        width:0;
        height:0;
    }

    .marker span {
        display:flex;
        justify-content:center;
        align-items:center;
        box-sizing:border-box;
        width: 30px;
        height: 30px;
        color:#fff;
        border:solid 2px;
        border-radius: 0 70% 70%;
        box-shadow:0 0 2px #000;
        cursor: pointer;
        transform-origin:0 0;
        transform: rotateZ(-135deg);
    }

    .marker b {
        transform: rotateZ(135deg)
    }

    .cartMarker span {
        background: $green-500;
    }

    .tableRowMarker span {
        background: $red-500;
    }

    .mapboxgl-popup {
        z-index: 999;
    }
}
</style>
